"use client";
import { Body, Heading } from "@/client/foundations/Typography";
// import { Box, Space, Stack } from "@fastlooptech/layout-components";
import { useResponsive } from "@/client/hooks/useResponsive";
// import { ButtonWrapper } from "@/components/ButtonWrapper";
// import { Icons, IconSetType } from "@/components/Icons";
import { ImageType, T_KeyValue } from "@/types/app.type";
import React, { ReactElement, useMemo, lazy } from "react";
import dynamic from 'next/dynamic';
// import { CardCarousel } from "./CardCarousel";
// import { CardVideo } from "./CardVideo";
// import IconSet from "@/components/Icons/IconSet";
import classNames from "classnames";
import { inputToNumber } from "@/helpers/utils.helper";
// import ImageWrapper from "@/components/ImageWrapper";

// const ImageWrapper = dynamic(() => import("@/components/ImageWrapper"), { ssr: false });
const CardCarousel = dynamic(() => import("./CardCarousel"), { ssr: false });
// const Video = dynamic(() => import("@/components/Video"), { ssr: false });
const Video = dynamic(()=> import("@/components/Video/Video"), { ssr: false });
const ImageWrapper = dynamic(()=> import("@/components/ImageWrapper"), { ssr: false });

export interface CardProps {
	"data-test-id"?: string;
	/**
	 * The Card id.
	 * @type string
	 */
	id?: string;
	/**
	 * The Card custom style.
	 * @type {T_KeyValue}
	 */
	styles?: React.CSSProperties;
	/**
	 * The Card className.
	 * @type {string}
	 */
	className?: string;
	/**
	 * Children is body of the Card.
	 * @type ReactElement
	 * @required
	 */
	children?: ReactElement;
	/**
	 * The title of the Card.
	 * @type string
	 * @required
	 */
	title?: string;
	/**
	 * The subtitle of the Card.
	 * @type string
	 * @optional
	 */
	subtitle?: string;
	/**
	 * The description of the Card.
	 * @type string
	 * @optional
	 */
	description?: string;
	/**
	 * Sub title font size.
	 * @type string
	 * @optional
	 */	
	subtitleFontSize?: 's' | 'm' | 'l' | 'xl';
	/**
	 * Title font size.
	 * @type string
	 * @optional
	 */	
	titleFontSize?: 's' | 'm' | 'l' | 'xl';
	/**
	 * Body font size.
	 * @type string
	 * @optional
	 */	
	bodyFontSize?: 'xxs' | 'xs' | 's' | 'm' | 'l' | 'xl';
	/**
	 * Hide buttons of the Card.
	 * @type boolean
	 */
	hideButtons?: boolean;
	/**
	 * Enable animation triggered whe the element is visible.
	 * @type boolean
	 * @optional
	 */
	animated?: boolean;
	/**
	 * The animation type triggered whe the element is visible. Default is fadeIn
	 * @type string
	 * @optional
	 */
	animation?: string;
	/**
	 * The action of the Card.
	 * @type () => void
	 * @optional
	 */
	// action?: () => void;
	/**
	 * Button 1 Label of the Card.
	 * @type string
	 * @optional
	 */
	btn1_label?: string;
	/**
	 * Button 1 Icon in the Card.
	 * @type any
	 * @optional
	 */
	btn1_icon?: any;
	/**
	 * Button 1 Icon Position
	 * @type string
	 * @optional
	 */
	btn1_iconPosition?: 'hide' | 'top' | 'right' | 'left';
	/**
	 * Button 1 Variant.
	 * @type string
	 * @optional
	 */
	btn1_variant?: string;
	/**
	 * Button 1 size.
	 * @type string
	 * @optional
	 */
	btn1_size?: string;
	/**
	 * The action of the Card.
	 * @type () => void
	 * @optional
	 */
	btn1_action?: () => void;
	/**
	 * Switch between different image positions or hide the image
	 * @type string
	 * @optional
	 */
	buttonUrl?: string;
	linkTargetBlank?: boolean;
	hideText?: boolean;
	// Media:
	/**
	 * Hide media of the Card.
	 * @type boolean
	 */
	hideMedia?: boolean;
	/**
	 * The images source the Card.
	 * @type ImageType[]
	 * @optional
	 */
	image?: ImageType;
	/**
	 * The images source the Card.
	 * @type ImageType[]
	 * @optional
	 */
	images?: ImageType[];
	/**
	 * The video source of the Card.
	 * @type string
	 * @optional
	 */
	video?: string;
	mobileVideo?: any,
	/**
	 * Color of the background behind media e.g. when image is smaller than it's wrapper
	 * @type string
	 * @optional
	 */
	imageBackgroundColor?: string;
	contentBackgroundColor?: string;
	mediaPosition?: "Normal" | "LeftEdge";
	mediaWidth?: string; //number;
	mediaHeight?: string; //number;
	mediaWrapperWidth?: string;
	mediaWrapperHeight?: string;
	mediaStartViewX?: string;
	mediaStartViewY?: string;
	centerTextVertically?: boolean;
	eventName?: string;
	playButtonPosition?: 'none' | 'center' | 'bottom-left' | 'top-right';
	pauseable?: boolean;
	autoPlay?: boolean;
	maxWidth?: string;
	mediaOverlay?: ReactElement;
	roundedCorners?: boolean;
	thumbnailOnly?: boolean;
	contentMinHeight?: string;
	contentMaxHeight?: string;
	childrenContent?: ReactElement;
	horizontalImageAlign?: string;
	droppableArea?: boolean;
	expProps?: any;
}

export default function DroppableAreaCard({
	hideText = false,
	centerTextVertically = false,
	hideButtons = false,
	btn1_label = "Show details",
	btn1_icon = 'Placeholder',
	btn1_iconPosition = "left",
	btn1_variant = "primary",
	btn1_size = "s",
	btn1_action = () => {},
	buttonUrl,
	linkTargetBlank = false,
	// Image:
	image = { width: 0, height: 0, src: "" },
	mediaWidth = "30px",
	mediaHeight = "30px",
	mediaPosition = "Normal",
	mediaStartViewX = '0px',
	mediaStartViewY = '0px',
	mediaWrapperWidth,
	mediaWrapperHeight,
	imageBackgroundColor = "#FFFFFF00",
	contentBackgroundColor = "#FFFFFF00",
	eventName,
	playButtonPosition,
	pauseable = false,
	autoPlay = false,
	maxWidth = "100%",
	mediaOverlay,
	roundedCorners = false,
	thumbnailOnly = false,
	contentMinHeight = "30px",
	contentMaxHeight = "auto",
	childrenContent,
	horizontalImageAlign = "justify-items-center",
	droppableArea = false,
	expProps,
	...rest
}: CardProps) {
	const { isMd } = useResponsive();
	const buttons = [
		{
			label: btn1_label,
			icon: btn1_icon,
			iconPosition: btn1_iconPosition,
			variant: btn1_variant,
			size: btn1_size,
			action: btn1_action,
		},
	];

	const CardMedia = useMemo(() => {
		if (rest?.images) {
			return (
				<CardCarousel
					data-test-id="card-carousel"
					images={rest?.images}
					style={{ width: image?.width || "0px", height: image?.height || "0px" }}
					// style={{width: mediaWrapperWidth || mediaWidth || "100%", height: mediaWrapperHeight || mediaHeight || "auto"}}
				/>
			);
		}

		// A component wrapping the media to be able to change position in frame
		const MediaWrapper = ({ children }: { children: React.ReactNode }) => ( //relative overflow-hidden shrink-0 w-auto h-auto , `${horizontalImageAlign}`
			<div 
				className={classNames("relative shrink-0 w-auto h-auto", `${horizontalImageAlign}`)} 
				style={{
					backgroundColor: imageBackgroundColor, 
					// width: mediaWidth || image?.width,
					// height: mediaHeight || image?.height,
				}}>
				{children}
			</div>
		);

		return (
			<MediaWrapper>
				{/* Img ---- {mediaWidth} ---- {mediaHeight} */}
				{/* <pre>{JSON.stringify(image, null, 2)}</pre> */}
				{/* {horizontalImageAlign} */}
				{/* ------- For video resources ------ */}
				{rest?.video ? (
					<>
						<Video
							className={`relative max-w-none [&>video]:object-cover`}
							style={{
								width: `${mediaWidth}`,
								height: `${mediaHeight}`,
								left: mediaStartViewX.startsWith('-') ? mediaStartViewX.slice(1) : `-${mediaStartViewX}`,
								top: mediaStartViewY.startsWith('-') ? mediaStartViewY.slice(1) :  `-${mediaStartViewY}`,
							}}
							data-test-id={rest["data-test-id"]}
							minHeight="100px"
							width={mediaWidth}
							height={mediaHeight}
							videoUrl={isMd ? rest?.video : rest?.mobileVideo}
							mobileVideo={rest?.mobileVideo}
							playButtonPosition={playButtonPosition}
							loop
							muted
							// playsinline
							pauseable={pauseable || false}
							autoPlay={autoPlay}
							eventName={eventName}
							showCustomPlayButton={true}
							thumbnailOnly={thumbnailOnly || false}
							fallback={
								<ImageWrapper
									src={image?.src}
									alt="card-image"
									placeholder="blur"
									blurDataURL={image?.src}
									width={image?.width || 50}
									height={image?.height || 50}
									priority
									// loading="lazy"
									loader={({ src }) => src}
									// style={rest?.style}
									style={{
										width: `${mediaWidth}`,
										height: `${mediaHeight}`,
										left: mediaStartViewX.startsWith('-') ? mediaStartViewX.slice(1) : `-${mediaStartViewX}`,
										top: mediaStartViewY.startsWith('-') ? mediaStartViewY.slice(1) :  `-${mediaStartViewY}`,
									}}
									className="!h-full object-cover max-w-none"
								/>
							}
						posterImage={image?.src} />
					</>
				) : (
					<>
						{/* ------- For image resources ------ */}
						<ImageWrapper
							src={image?.src}
							// width={mediaWidth || image?.width}
							// height={mediaHeight || image?.height} // not important but required to omit  build errors
							width={inputToNumber(mediaWidth) || image?.width}
							height={inputToNumber(mediaHeight) || image?.height} // not important but required to omit  build errors
							alt="card-image"
							placeholder="blur"
							blurDataURL={image?.src}
							// loading="lazy"
							loader={({ src }) => src}
							className={classNames(
								"absolute1 object-cover max-w-none"
							)}
							style={{
								width: mediaWidth,
								height: mediaHeight,
								left: mediaStartViewX.startsWith('-') ? mediaStartViewX.slice(1) : `-${mediaStartViewX}`,
								top: mediaStartViewY.startsWith('-') ? mediaStartViewY.slice(1) :  `-${mediaStartViewY}`,
								// borderTopLeftRadius: mediaPosition === 'Normal' ?  : 0,
								// (mediaPosition === 'Normal' ? "border-radius-top-left" : (mediaPosition === 'LeftEdge' ? "flex-row" : (mediaPosition === 'Right' ? "flex-row-reverse" : "flex-col-reverse")))
							}}
							data-aos={rest?.animated ? rest?.animation ?? "fade-in" : ""}
						/>
					</>
				)}
			</MediaWrapper>
		);
	}, [
		image,
		rest,
		mediaStartViewX,
		mediaStartViewY,
		mediaWrapperWidth,
		mediaWrapperHeight,
		// mediaBackgroundColor,
		mediaWidth,
		mediaHeight,
		playButtonPosition,
		autoPlay,
		mediaPosition,
		pauseable,
	]);

	const onAction = () => {
		if (window && eventName) {
		  window.dispatchEvent(new Event(eventName));
		}
	}

	return (
		<div
			id={"dcd"}
			data-test-id={rest["data-test-id"]}
			className={classNames(
				rest?.className, 
				expProps?.className,
				"flex relative", 
				"overlow-hidden shrink-0", 
				`${eventName ? "cursor-pointer" : ""}`, 
				(mediaPosition === 'Normal' ? "flex-col" : (mediaPosition === 'LeftEdge' ? "flex-row" : (mediaPosition === 'Right' ? "flex-row-reverse" : "flex-col-reverse"))),
				`cfstyles-${expProps["data-cf-node-id"]}`,
				// {
				// 	// "flex-col": mediaPosition !== "LeftEdge",
				// 	'rounded-r-image-card': roundedCorners && mediaPosition === 'LeftEdge',
				// 	'rounded-b-image-card': roundedCorners && mediaPosition === 'Normal',
				// 	'rounded-image-card': roundedCorners && rest?.hideMedia,
				// }
			)}
			onClick={onAction}
			{...(()=>{return eventName ? { title: "Click here to open it." }: {}})()}
			// style={{maxWidth, width: mediaWrapperWidth || mediaWidth || "100%", minHeight: mediaWrapperHeight || mediaHeight || "auto", ...rest?.styles}}
			style={{maxWidth, ...(rest?.styles || {})}}
			// data-cf-node-id={expProps["data-cf-node-id"]}
			// {...expProps}
		>
			{/* <pre>{JSON.stringify(expProps, null, 2)}</pre> */}
			{rest?.children ? (
				<>
					{/* ------------------------------------------------------------------------------------------
					------------ If card is used only as a wrapper/container for provided children -----------
					------------------------------------------------------------------------------------------ */}
					{rest?.children}
				</>
			) : (
				<>
					{/* ---------------------------------------------------------------------------------------------
					------------ If card is used with props (title, subtitle, hideMedia, video, ... ) -----------
					--------------------------------------------------------------------------------------------- */}

					{/* ------- Show/Hide media section ------- */}
					{!rest?.hideMedia && CardMedia}

					{/* ------- Show/Hide text content based on props value (if props cantain value then show otherwish hide) ------- */}
					{!hideText && (
						<div className={classNames(
								// "relative",
								// "md:py-cardY", 
								// "px-cardX", 
								"flex flex-col items-start gap-card-text-gap"
							)}>
							<div className={classNames(
								"md:py-cardY", 
								"px-cardX", 
								"flex flex-col items-start gap-card-text-gap", 
								"top-panel",
								{
									"py-[10px]": rest?.video,
									"py-cardY": !rest?.video,
									"my-auto": centerTextVertically,
								},
								)}
								style={{minHeight: contentMinHeight, maxHeight: contentMaxHeight}}
							>
								{/* <>{contentMinHeight}</> */}

								{/* ------ Title/Main Heading ------ */}
								{rest?.title && (
									<Heading
										size={rest?.subtitleFontSize || "m"}
										animated={rest?.animated ?? false}
										className={classNames('!font-image-card-header')}
										animation={rest?.animation ?? ""}>
										{rest?.title || ""}
									</Heading>
								)}

								{/* ------ Subtitle/Subheading ------ */}
								{rest?.subtitle && (
									<Heading
										size={rest?.titleFontSize || "s"}
										className={classNames('!font-image-card-sub-header')}
										animated={rest?.animated ?? false}
										animation={rest?.animation ?? ""}>
										{rest?.subtitle || ""}
									</Heading>
								)}

								{/* ------ Description ----- */}
								{rest?.description && (
									<Body
										size={rest?.bodyFontSize || "l"}
										animated={rest?.animated ?? false}
										animation={rest?.animation ?? ""}>
										{rest?.description || ""}
									</Body>
								)}
							</div>
							{droppableArea && 
								<div className={"bottom-panel w-full h-auto bg-origin-border bg-no-repeat bg-auto"}>
									{/* ------ Droppable area ------ */}
									{childrenContent}
								</div>
							}
						</div>
					)}
				</>
			)}
		</div>
	);
}
